import {
	useGetSubscriptionTierConfigsQuery,
	useUpdateSubscriptionTierConfigMutation,
	SubscriptionTier,
} from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NumberInput } from '../../components/ui/number-input';
import { Switch } from '../../components/ui/switch';

/* ── Tier card ── */

interface TierFormState {
	kickbackRate: string;
	aiTokenRatePerMillion: string;
	stripePriceId: string;
	isActive: boolean;
}

function TierCard({
	tier,
	monthlyPrice,
	currency,
	initialKickback,
	initialAiRate,
	initialStripePriceId,
	initialIsActive,
	onRefetch,
}: {
	tier: SubscriptionTier;
	monthlyPrice?: number | null;
	currency?: string | null;
	initialKickback: number;
	initialAiRate: number | null;
	initialStripePriceId: string | null;
	initialIsActive: boolean;
	onRefetch: () => void;
}) {
	const [form, setForm] = useState<TierFormState>({
		kickbackRate: String(initialKickback * 100),
		aiTokenRatePerMillion: initialAiRate != null ? String(initialAiRate) : '',
		stripePriceId: initialStripePriceId || '',
		isActive: initialIsActive,
	});

	const [updateConfig, { loading }] = useUpdateSubscriptionTierConfigMutation();

	useEffect(() => {
		setForm({
			kickbackRate: String(initialKickback * 100),
			aiTokenRatePerMillion: initialAiRate != null ? String(initialAiRate) : '',
			stripePriceId: initialStripePriceId || '',
			isActive: initialIsActive,
		});
	}, [initialKickback, initialAiRate, initialStripePriceId, initialIsActive]);

	const hasChanges =
		form.kickbackRate !== String(initialKickback * 100) ||
		form.aiTokenRatePerMillion !== (initialAiRate != null ? String(initialAiRate) : '') ||
		form.stripePriceId !== (initialStripePriceId || '') ||
		form.isActive !== initialIsActive;

	const handleSave = async () => {
		try {
			await updateConfig({
				variables: {
					tier,
					kickbackRate: parseFloat(form.kickbackRate) / 100 || 0,
					aiTokenRatePerMillion: form.aiTokenRatePerMillion
						? parseFloat(form.aiTokenRatePerMillion)
						: null,
					stripePriceId: form.stripePriceId || null,
					isActive: form.isActive,
				},
			});
			toast.success(`${tier} tier updated`);
			onRefetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update tier');
		}
	};

	const priceDisplay =
		monthlyPrice != null
			? `${currency?.toUpperCase() || ''} ${monthlyPrice.toFixed(2)}`
			: 'Not set';

	return (
		<Card className="border-line-1 bg-surface gap-0 py-0">
			<div className="flex items-center justify-between border-b border-line-1 px-5 py-3">
				<div className="flex items-center gap-2">
					<h3 className="text-base font-medium text-text-1">{tier}</h3>
					{!form.isActive && (
						<span className="rounded-full bg-status-neutral-soft px-2 py-0.5 text-2xs font-medium text-text-3">
							Inactive
						</span>
					)}
				</div>
				<div className="text-sm font-mono text-text-3">{priceDisplay}/mo</div>
			</div>
			<div className="flex flex-col gap-4 p-5">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="flex flex-col gap-1.5">
						<Label>Kickback rate (%)</Label>
						<NumberInput
							value={form.kickbackRate}
							onChange={(e) =>
								setForm((f) => ({ ...f, kickbackRate: e.target.value }))
							}
							placeholder="0"
							min={0}
							max={100}
							step={0.5}
						/>
					</div>
					<div className="flex flex-col gap-1.5">
						<Label>AI token rate ($/1M)</Label>
						<NumberInput
							value={form.aiTokenRatePerMillion}
							onChange={(e) =>
								setForm((f) => ({ ...f, aiTokenRatePerMillion: e.target.value }))
							}
							placeholder="Not set"
							min={0}
							step={0.01}
						/>
					</div>
				</div>

				<div className="flex flex-col gap-1.5">
					<Label>Stripe price ID</Label>
					<Input
						value={form.stripePriceId}
						onChange={(e) => setForm((f) => ({ ...f, stripePriceId: e.target.value }))}
						placeholder="price_1ABC..."
					/>
				</div>

				<div className="flex items-center justify-between rounded-lg border border-line-2 px-3 py-2.5">
					<div>
						<Label className="text-sm">Active</Label>
						<p className="text-sm text-muted-foreground">
							Make this tier available to organizations
						</p>
					</div>
					<Switch
						checked={form.isActive}
						onCheckedChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
					/>
				</div>

				<div className="flex justify-end border-t border-line-1 pt-4">
					<Button onClick={handleSave} disabled={!hasChanges || loading}>
						{loading ? 'Saving…' : 'Save changes'}
					</Button>
				</div>
			</div>
		</Card>
	);
}

/* ── Main tab ── */

const TIER_ORDER: SubscriptionTier[] = [
	SubscriptionTier.Creator,
	SubscriptionTier.Pro,
	SubscriptionTier.Enterprise,
];

export function SubscriptionPricingTab() {
	const { data, loading, refetch } = useGetSubscriptionTierConfigsQuery({
		fetchPolicy: 'no-cache',
	});

	if (loading) {
		return (
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
				{Array.from({ length: 3 }).map((_, i) => (
					<Card key={i} className="border-line-1 bg-surface gap-0 py-0">
						<div className="space-y-4 p-5">
							<div className="h-5 w-24 animate-pulse rounded bg-surface-fill" />
							<div className="h-8 w-full animate-pulse rounded bg-surface-fill" />
							<div className="h-8 w-full animate-pulse rounded bg-surface-fill" />
						</div>
					</Card>
				))}
			</div>
		);
	}

	const configs = data?.getSubscriptionTierConfigs ?? [];
	const sorted = [...configs].sort(
		(a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier)
	);

	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
			{sorted.map((config) => (
				<TierCard
					key={config.tier}
					tier={config.tier}
					monthlyPrice={config.monthlyPrice}
					currency={config.currency}
					initialKickback={config.kickbackRate}
					initialAiRate={config.aiTokenRatePerMillion ?? null}
					initialStripePriceId={config.stripePriceId ?? null}
					initialIsActive={config.isActive}
					onRefetch={refetch}
				/>
			))}
		</div>
	);
}
